/**
 * Label / Country multiselects for the filter popup (screens 3–4): a field
 * that shows the picked values as chips (3, then `+N`), and a panel with a
 * search box and checkboxes. The panel is a layer (Escape closes it before
 * the popup) and the popup keeps at most one panel open at a time — the
 * `open` / `onOpenChange` pair belongs to the caller.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { I } from "@/components/Icon";
import { useLayer } from "@/lib/layer-stack";
import { flagOf, rankCountries } from "@/lib/ads-filter-countries";

/** `badge` (fix2): a status word rendered next to the name in the
 *  existing status-badge style — the Rules picker marks paused rules. */
export type LabelOption = { id: number; name: string; count: number; badge?: string };
export type { CountryOption } from "@/lib/ads-filter-countries";
import type { CountryOption } from "@/lib/ads-filter-countries";

const MAX_CHIPS = 3;

function Chev() {
  return (
    <span className="af-ms-chev">
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="m6 9 6 6 6-6" /></svg>
    </span>
  );
}

function ChipX({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      className="af-ms-chip-x"
      aria-label="Remove"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
    >
      <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><path d="M6 6l12 12" /><path d="M6 18 18 6" /></svg>
    </button>
  );
}

function usePanel(open: boolean, onOpenChange: (o: boolean) => void, ref: React.RefObject<HTMLElement | null>) {
  useLayer(open, () => onOpenChange(false));
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onOpenChange(false);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open, onOpenChange, ref]);
}

export function LabelMultiSelect({
  options,
  selected,
  onChange,
  open,
  onOpenChange,
  noun = "labels",
  emptyText = "No labels at this level",
}: {
  options: LabelOption[];
  selected: number[];
  onChange: (next: number[]) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** "labels" | "rules" — placeholder + search copy; everything else is shared. */
  noun?: string;
  emptyText?: string;
}) {
  const [q, setQ] = useState("");
  const ref = useRef<HTMLSpanElement>(null);
  usePanel(open, onOpenChange, ref);
  useEffect(() => { if (!open) setQ(""); }, [open]);
  const byId = useMemo(() => new Map(options.map((o) => [o.id, o])), [options]);
  const needle = q.trim().toLowerCase();
  const list = needle ? options.filter((o) => o.name.toLowerCase().includes(needle)) : options;
  const toggle = (id: number) =>
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const shown = selected.slice(0, MAX_CHIPS);
  const extra = selected.length - shown.length;
  return (
    <span className="af-ms" ref={ref}>
      <span className="af-ms-field" data-open={open ? "true" : "false"} onClick={() => onOpenChange(!open)}>
        {selected.length === 0 && <span className="af-ms-placeholder">Select {noun}</span>}
        {shown.map((id) => (
          <span key={id} className="af-ms-chip">
            {byId.get(id)?.name ?? `#${id}`}
            <ChipX onClick={() => toggle(id)} />
          </span>
        ))}
        {extra > 0 && <span className="af-ms-more">+{extra}</span>}
        <Chev />
      </span>
      {open && (
        <span className="af-ms-panel" onClick={(e) => e.stopPropagation()}>
          <span className="af-ms-search">
            {I.search}
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${noun}`} />
          </span>
          <span className="af-ms-list">
            {list.map((o) => (
              <label key={o.id} className="af-ms-opt">
                <input type="checkbox" checked={selected.includes(o.id)} onChange={() => toggle(o.id)} />
                <span>{o.name}</span>
                {o.badge && <span className="badge paused af-ms-badge">{o.badge}</span>}
                <span className="count">{o.count}</span>
              </label>
            ))}
            {list.length === 0 && (
              <span className="af-ms-empty">{options.length === 0 ? emptyText : "No matches"}</span>
            )}
          </span>
        </span>
      )}
    </span>
  );
}

export function CountryMultiSelect({
  options,
  selected,
  onChange,
  open,
  onOpenChange,
}: {
  options: CountryOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [q, setQ] = useState("");
  const ref = useRef<HTMLSpanElement>(null);
  usePanel(open, onOpenChange, ref);
  useEffect(() => { if (!open) setQ(""); }, [open]);
  const list = useMemo(() => rankCountries(options, q), [options, q]);
  const toggle = (code: string) =>
    onChange(selected.includes(code) ? selected.filter((x) => x !== code) : [...selected, code]);
  const shown = selected.slice(0, MAX_CHIPS);
  const extra = selected.length - shown.length;
  const needle = q.trim();
  return (
    <span className="af-ms" ref={ref}>
      <span className="af-ms-field" data-open={open ? "true" : "false"} onClick={() => onOpenChange(!open)}>
        {selected.length === 0 && <span className="af-ms-placeholder">Select countries</span>}
        {shown.map((code) => (
          <span key={code} className="af-ms-chip">
            <span style={{ fontSize: 12 }}>{flagOf(code)}</span>
            <span className="iso">{code}</span>
            <ChipX onClick={() => toggle(code)} />
          </span>
        ))}
        {extra > 0 && <span className="af-ms-more">+{extra}</span>}
        <Chev />
      </span>
      {open && (
        <span className="af-ms-panel" onClick={(e) => e.stopPropagation()}>
          <span className="af-ms-search">
            {I.search}
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Country name or ISO code" />
          </span>
          <span className="af-ms-hint">
            {needle ? `${list.length} matches for “${needle}”` : "Type a name or an ISO code"}
          </span>
          <span className="af-ms-list">
            {list.map((c) => (
              <label key={c.code} className="af-ms-opt">
                <input type="checkbox" checked={selected.includes(c.code)} onChange={() => toggle(c.code)} />
                <span className="flag">{flagOf(c.code)}</span>
                <span className="iso">{c.code}</span>
                <span>{c.name}</span>
              </label>
            ))}
            {list.length === 0 && <span className="af-ms-empty">No matches</span>}
          </span>
        </span>
      )}
    </span>
  );
}
